import api from "./apiHelper";
import { queryCache } from "react-query";
import { useCustomPaginatedQuery, useCustomQuery } from "hooks/reactQuery";

export const postCreateAdviseeBaseProfile = (payload) => {
  return api.post(`/advisee/base-profile`, payload).then((advisee) => {
    refetchAll(advisee._id);
    return advisee;
  });
};

export const postCreateAdviseeOwnedPolicy = (adviseeId, payload) => {
  return api.post(`/advisee/${adviseeId}/policy`, payload).then((res) => {
    refetchAll(adviseeId);
    return res;
  });
};

export const postCreateAdviseeProposal = (adviseeId, payload) => {
  return api.post(`/advisee/${adviseeId}/proposal`, payload).then((res) => {
    refetchAdviseeProposals(adviseeId);
    return res;
  });
};

export const useGetAdvisees = (query, onError) => {
  const getAdvisees = (key, query) => {
    return api.get(`/advisee/aggregation`, {
      params: { ...query },
    });
  };
  return useCustomPaginatedQuery("advisees", getAdvisees, query, {
    onError,
  });
};

export const useGetAdviseeSuggestions = (adviseeId, viewingFamilyMemberId) => {
  const getAdviseeDetails = () => {
    return api.get(`/advisee/${adviseeId}/family-member/suggestions`, {
      params: { viewingFamilyMemberId },
    });
  };
  return useCustomQuery("adviseeSuggestions", [], getAdviseeDetails, {
    shouldDefaultEmptyObject: false,
  });
};

export const useGetAdviseeDetails = (adviseeId, query = {}, onError) => {
  const getAdviseeDetails = (key, adviseeId, query) => {
    return api.get(`/advisee/${adviseeId}`, {
      params: { ...query },
    });
  };
  return useCustomQuery(
    "adviseeDetails",
    [adviseeId, query],
    getAdviseeDetails,
    {
      onError,
      shouldFetch: !!adviseeId,
    }
  );
};

export const useGetAdviseePersonalInfo = (adviseeId, query = {}, onError) => {
  const getAdviseePersonalInfo = (key, adviseeId, query) => {
    return api.get(`advisee/${adviseeId}/personal-info`, {
      params: { ...query },
    });
  };

  return useCustomQuery(
    "adviseePersonalInfo",
    [adviseeId, query],
    getAdviseePersonalInfo,
    { onError, shouldFetch: !!adviseeId }
  );
};

export const useGetAdviseeReporting = (adviseeId, onError) => {
  const getAdviseeReporting = (key, adviseeId) => {
    return api.get(`advisee/${adviseeId}/reporting`);
  };

  return useCustomQuery("adviseeReporting", [adviseeId], getAdviseeReporting, {
    onError,
    shouldFetch: !!adviseeId,
  });
};

export const useGetAdviseeProposalReporting = (
  adviseeId,
  proposalId,
  onError
) => {
  const getAdviseeProposalReporting = (key, adviseeId, proposalId) => {
    return api.get(`advisee/${adviseeId}/proposal/${proposalId}/riskRatio`);
  };

  return useCustomQuery(
    "adviseeProposalReporting",
    [adviseeId, proposalId],
    getAdviseeProposalReporting,
    { onError, shouldFetch: !!adviseeId && !!proposalId }
  );
};

export const useGetAdviseeProposals = (adviseeId, onError) => {
  const getAdviseeProposals = (key, adviseeId) => {
    return api.get(`advisee/${adviseeId}/proposal`);
  };

  return useCustomQuery("adviseeProposals", [adviseeId], getAdviseeProposals, {
    onError,
    shouldFetch: !!adviseeId,
  });
};

export const useGetAdviseeProposal = (adviseeId, proposalId, onError) => {
  const getAdviseeProposal = () => {
    return api.get(`advisee/${adviseeId}/proposal/${proposalId}`);
  };

  return useCustomQuery(
    "adviseeProposal",
    [adviseeId, proposalId],
    getAdviseeProposal,
    { onError, shouldFetch: !!adviseeId && !!proposalId }
  );
};

export const useGetAdviseePolicy = (adviseeId, policyId, onError) => {
  const getAdviseePolicy = (key, adviseeId, policyId) => {
    return api.get(`advisee/${adviseeId}/policy/${policyId}`);
  };

  return useCustomQuery(
    "adviseePolicy",
    [adviseeId, policyId],
    getAdviseePolicy,
    { onError, shouldFetch: !!adviseeId && !!policyId }
  );
};

export const useGetAdviseeCashFlowSummary = (adviseeId, onError) => {
  const getAdviseeCashFlowSummary = (key, adviseeId) => {
    return api.get(`advisee/${adviseeId}/cashflow`);
  };

  return useCustomQuery(
    "adviseeCashFlow",
    [adviseeId],
    getAdviseeCashFlowSummary,
    { onError, shouldFetch: !!adviseeId }
  );
};

export const useGetAdviseeEstatePlanning = (adviseeId, onError) => {
  const getAdviseeEstatePlannings = (key, adviseeId) => {
    return api.get(`advisee/${adviseeId}/estate-planning`);
  };

  return useCustomQuery(
    "adviseeEstatePlanning",
    [adviseeId],
    getAdviseeEstatePlannings,
    { onError, shouldFetch: !!adviseeId }
  );
};

export const useGetAdviseeInvestmentPortfolio = (adviseeId, onError) => {
  const getAdviseeInvestmentPortfolio = (key, adviseeId) => {
    return api.get(`advisee/${adviseeId}/investment-portfolio`);
  };

  return useCustomQuery(
    "adviseeInvestmentPortfolio",
    [adviseeId],
    getAdviseeInvestmentPortfolio,
    { onError, shouldFetch: !!adviseeId }
  );
};

export const useGetAdviseeActionPlans = (adviseeId, onError) => {
  const getAdviseeActionPlans = (key, adviseeId) => {
    return api.get(`advisee/${adviseeId}/action-plan`);
  };

  return useCustomQuery(
    "adviseeActionPlans",
    [adviseeId],
    getAdviseeActionPlans,
    { onError, shouldFetch: !!adviseeId, shouldDefaultEmptyObject: false }
  );
};

export const useGetAdviseeFutureReviews = (adviseeId, onError) => {
  const getAdviseeFutureReviews = (key, adviseeId) => {
    return api.get(`advisee/${adviseeId}/review`);
  };

  return useCustomQuery(
    "adviseeFutureReviews",
    [adviseeId],
    getAdviseeFutureReviews,
    { onError, shouldFetch: !!adviseeId, shouldDefaultEmptyObject: false }
  );
};

export const patchAdvisee = (adviseeId, payload) => {
  return api.patch(`/advisee/${adviseeId}`, payload).then((res) => {
    refetchAll(adviseeId);
    return res;
  });
};

export const patchAddFamilyMember = (adviseeId, payload) => {
  return api
    .patch(`/advisee/${adviseeId}/family-member/`, payload, {
      params: { action: "add" },
    })
    .then((res) => {
      return res;
    });
};

export const patchUpdateFamilyMember = (adviseeId, familyMemberId, payload) => {
  return api
    .patch(`/advisee/${adviseeId}/family-member`, payload, {
      params: { action: "update", familyMemberId },
    })
    .then((res) => {
      return res;
    });
};

export const patchRemoveFamilyMember = (adviseeId, familyMemberId) => {
  return api
    .patch(`/advisee/${adviseeId}/family-member`, null, {
      params: { action: "remove", familyMemberId },
    })
    .then((res) => {
      return res;
    });
};

export const patchAdviseePolicy = (adviseeId, policyId, payload) => {
  return api
    .patch(`advisee/${adviseeId}/policy/${policyId}`, payload)
    .then((res) => {
      refetchAll(adviseeId);
      return res;
    });
};

export const patchAdviseeCashFlowInQuickManage = (adviseeId, payload) => {
  return api
    .patch(`/advisee/${adviseeId}/cashflow/quick-manage`, payload)
    .then((res) => {
      refetchAll(adviseeId);
      return res;
    });
};

export const patchAdviseeCashInflow = (adviseeId, payload) => {
  return api
    .patch(`/advisee/${adviseeId}/cashflow/income`, payload)
    .then((res) => {
      refetchAll(adviseeId);
      return res;
    });
};

export const patchAdviseeCashOutflow = (adviseeId, payload) => {
  return api
    .patch(`/advisee/${adviseeId}/cashflow/expenses`, payload)
    .then((res) => {
      refetchAll(adviseeId);
      return res;
    });
};

export const patchAdviseeAttachment = (adviseeId, payload) => {
  return api.patch(`/advisee/${adviseeId}/attachment`, payload).then((res) => {
    refetchAll(adviseeId);
    return res;
  });
};

export const patchAdviseeProfileAttachment = (adviseeId, payload) => {
  return api
    .patch(`/advisee/${adviseeId}/profile/attachment`, payload)
    .then((res) => {
      refetchAll(adviseeId);
      return res;
    });
};

export const patchAdviseeEstatePlanning = (adviseeId, type, payload) => {
  return api
    .patch(`/advisee/${adviseeId}/estate-planning/${type}`, payload)
    .then((res) => {
      refetchAll(adviseeId);
      return res;
    });
};

export const patchAdviseeInvestmentPortfolio = (adviseeId, type, payload) => {
  return api
    .patch(`/advisee/${adviseeId}/investment-portfolio/${type}`, payload)
    .then((res) => {
      refetchAll(adviseeId);
      return res;
    });
};

export const patchAdviseeActionPlans = (adviseeId, payload) => {
  return api.patch(`/advisee/${adviseeId}/action-plan`, payload).then((res) => {
    refetchAll(adviseeId);
    return res;
  });
};

export const patchAdviseeFutureReviews = (adviseeId, payload) => {
  return api.patch(`/advisee/${adviseeId}/review`, payload).then((res) => {
    refetchAll(adviseeId);
    return res;
  });
};

export const patchAdviseeProposalSettings = (
  adviseeId,
  proposalId,
  payload
) => {
  return api
    .patch(`/advisee/${adviseeId}/proposal/${proposalId}`, payload)
    .then((res) => {
      refetchAdviseeProposals(adviseeId);
      return res;
    });
};

export const patchAdviseeProposalPolicyStatuses = (
  adviseeId,
  proposalId,
  payload
) => {
  return api
    .patch(
      `/advisee/${adviseeId}/proposal/${proposalId}/policy-status`,
      payload
    )
    .then((res) => {
      refetchAdviseeProposal(adviseeId, proposalId);
      return res;
    });
};

export const patchAddedProposalPolicies = (adviseeId, proposalId, payload) => {
  return api
    .patch(`/advisee/${adviseeId}/proposal/${proposalId}/policy`, payload)
    .then((res) => {
      refetchAdviseeProposal(adviseeId, proposalId);
      return res;
    });
};

export const patchExistingProposalPolicies = (
  adviseeId,
  proposalId,
  policyId,
  payload
) => {
  return api
    .patch(
      `/advisee/${adviseeId}/proposal/${proposalId}/policy/${policyId}`,
      payload
    )
    .then((res) => {
      refetchAdviseeProposal(adviseeId, proposalId);
      return res;
    });
};

export const patchResetExistingProposalPolicies = (
  adviseeId,
  proposalId,
  policyId
) => {
  return api
    .patch(
      `/advisee/${adviseeId}/proposal/${proposalId}/policy/${policyId}/reset`
    )
    .then((res) => {
      refetchAdviseeProposal(adviseeId, proposalId);
      return res;
    });
};

export const deleteAdvisee = (adviseeId) => {
  return api.delete(`advisee/${adviseeId}`);
};

export const deleteAdviseePolicy = (adviseeId, policyId) => {
  return api.delete(`advisee/${adviseeId}/policy/${policyId}`);
};

export const deleteAdviseeProposal = (adviseeId, proposalId) => {
  return api
    .delete(`advisee/${adviseeId}/proposal/${proposalId}`)
    .then((res) => {
      refetchAdviseeProposals(adviseeId);
      return res;
    });
};

export const duplicateAdviseeProposal = (adviseeId, proposalId, payload) => {
  return api
    .patch(`advisee/${adviseeId}/duplicate/proposal/${proposalId}`, payload)
    .then((res) => {
      refetchAdviseeProposals(adviseeId);
      return res;
    });
};

export const refetchAdvisees = () => {
  queryCache.refetchQueries("advisees");
};

export const refetchAdviseePersonalInfo = (id) => {
  queryCache.refetchQueries(["adviseePersonalInfo", id]);
};

export const refetchAdviseeReporting = (id) => {
  queryCache.refetchQueries(["adviseeReporting", id]);
};

export const refetchAdviseeDetails = (id) => {
  queryCache.refetchQueries(["adviseeDetails", id]);
};

export const refetchAdviseeCashFlowDetails = (id) => {
  queryCache.refetchQueries(["adviseeCashFlow", id]);
};

export const refetchAdviseeEstatePlanningDetails = (id) => {
  queryCache.refetchQueries(["adviseeEstatePlanning", id]);
};

export const refetchAdviseeInvestmentPortfolioDetails = (id) => {
  queryCache.refetchQueries(["adviseeInvestmentPortfolio", id]);
};

export const refetchAdviseeActionPlans = (id) => {
  queryCache.refetchQueries(["adviseeActionPlans", id]);
};

export const refetchAdviseeFutureReviews = (id) => {
  queryCache.refetchQueries(["adviseeFutureReviews", id]);
};

export const refetchAdviseeProposals = (id) => {
  queryCache.refetchQueries(["adviseeProposals", id]);
};

export const refetchAdviseeProposal = (adviseeId, proposalId) => {
  queryCache.refetchQueries(["adviseeProposal", adviseeId, proposalId]);
};

const refetchAll = (id) => {
  refetchAdviseePersonalInfo(id);
  refetchAdviseeReporting(id);
  refetchAdviseeDetails(id);
  refetchAdviseeCashFlowDetails(id);
  refetchAdviseeEstatePlanningDetails(id);
  refetchAdviseeInvestmentPortfolioDetails(id);
  refetchAdviseeActionPlans(id);
  refetchAdviseeFutureReviews(id);
};
